import React, { useEffect, useRef, useState } from "react";
import { createChart } from "lightweight-charts";

/**
 * CandlesChart - muestra velas, SuperTrend por segmentos coloreados,
 * markers en flips y panel OHLCV. Tema negro/neón.
 */
export default function CandlesChart({
  candles = [],
  superTrendPeriod = 10,
  superTrendMultiplier = 3,
  height = 400,
  decimalPlaces = 2,
}) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const stSegmentSeriesRef = useRef([]);
  const isUnmountedRef = useRef(false);
  const rafRef = useRef(null);
  const latestCandlesRef = useRef([]);
  const [hoverInfo, setHoverInfo] = useState(null);
  const isHoveringRef = useRef(false);

  // Helper function to calculate minMove
  const getMinMove = (precision) => {
    if (precision <= 0) return 1;
    return parseFloat(`0.${'0'.repeat(precision - 1)}1`);
  };

  // SuperTrend calc (unchanged)
  function computeSuperTrend(data, period, multiplier) {
    if (!data || data.length < period + 1) return [];

    const tr = new Array(data.length).fill(0);
    for (let i = 1; i < data.length; i++) {
      const high = data[i].high;
      const low = data[i].low;
      const prevClose = data[i - 1].close;
      const trueRange = Math.max(
        high - low,
        Math.abs(high - prevClose),
        Math.abs(low - prevClose),
      );
      tr[i] = trueRange;
    }

    const atr = new Array(data.length).fill(null);
    let sum = 0;
    for (let i = 1; i <= period; i++) sum += tr[i] || 0;
    atr[period] = sum / period;
    for (let i = period + 1; i < data.length; i++) {
      atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
    }

    const basicUpper = new Array(data.length).fill(null);
    const basicLower = new Array(data.length).fill(null);
    const finalUpper = new Array(data.length).fill(null);
    const finalLower = new Array(data.length).fill(null);
    const supertrend = new Array(data.length).fill(null);
    const trend = new Array(data.length).fill(null);

    for (let i = period; i < data.length; i++) {
      const hl2 = (data[i].high + data[i].low) / 2;
      const curAtr = atr[i] || 0;
      basicUpper[i] = hl2 + multiplier * curAtr;
      basicLower[i] = hl2 - multiplier * curAtr;

      if (i === period) {
        finalUpper[i] = basicUpper[i];
        finalLower[i] = basicLower[i];
        supertrend[i] =
          data[i].close > finalUpper[i] ? finalLower[i] : finalUpper[i];
        trend[i] = data[i].close > finalUpper[i] ? "up" : "down";
        continue;
      }

      if (
        basicUpper[i] < finalUpper[i - 1] ||
        data[i - 1].close > finalUpper[i - 1]
      ) {
        finalUpper[i] = basicUpper[i];
      } else {
        finalUpper[i] = finalUpper[i - 1];
      }

      if (
        basicLower[i] > finalLower[i - 1] ||
        data[i - 1].close < finalLower[i - 1]
      ) {
        finalLower[i] = basicLower[i];
      } else {
        finalLower[i] = finalLower[i - 1];
      }

      if (data[i].close > finalUpper[i]) {
        trend[i] = "up";
        supertrend[i] = finalLower[i];
      } else if (data[i].close < finalLower[i]) {
        trend[i] = "down";
        supertrend[i] = finalUpper[i];
      } else {
        trend[i] = trend[i - 1] || "down";
        supertrend[i] = trend[i] === "up" ? finalLower[i] : finalUpper[i];
      }
    }

    return data.map((d, i) =>
      supertrend[i] != null
        ? { time: d.time, value: supertrend[i], trend: trend[i] }
        : null,
    );
  }

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    isUnmountedRef.current = false;

    // create chart with black layout
    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      layout: {
        background: { color: "#000000" },
        backgroundColor: "#000000",
        textColor: "#00f5d4",
      },
      grid: {
        vertLines: { color: "rgba(0,255,200,0.04)" },
        horzLines: { color: "rgba(0,255,200,0.03)" },
      },
      crosshair: { color: "rgba(0,255,200,0.45)" },
      rightPriceScale: {
        borderColor: "rgba(0,255,200,0.12)",
        scaleMargins: { top: 0.1, bottom: 0.1 },
      priceFormat: {
        type: 'price',
        precision: decimalPlaces,
        minMove: getMinMove(decimalPlaces),
      },
      },
      timeScale: {
        borderColor: "rgba(0,255,200,0.12)",
        rightOffset: 10,
        barSpacing: 6,
      },
    });

    // ensure the container DOM has the class (so CSS rules apply)
    try {
      el.classList.add("chart-container");
    } catch (e) {
      /* ignore */
    }
    // reapply layout to be safe
    try {
      chart.applyOptions({ layout: { background: { color: "#000000" } } });
    } catch (e) {
      /* ignore */
    }

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#00ff85",
      downColor: "#ff2d55",
      wickUpColor: "#00ffd0",
      wickDownColor: "#ff7aa2",
      borderVisible: false,
      wickVisible: true,
      priceFormat: {
        type: 'price',
        precision: decimalPlaces,
        minMove: getMinMove(decimalPlaces),
      },
    });
    candleSeriesRef.current = candleSeries;

    chartRef.current = chart;

    // subscribe crosshair; capture unsubscribe function
    const unsubscribeCross = chart.subscribeCrosshairMove((param) => {
      if (!param) {
        isHoveringRef.current = false;
        setHoverInfo(null);
        return;
      }
      const data = param.seriesData?.get(candleSeriesRef.current);
      if (data) {
        isHoveringRef.current = true;
        setHoverInfo({
          time: param.time ?? data.time,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
          volume: data.volume ?? null,
        });
        return;
      }
      const t = param.time;
      if (t != null) {
        let ts = null;
        if (typeof t === "number") ts = t;
        else {
          const parsed = new Date(t);
          if (!isNaN(parsed)) ts = Math.floor(parsed.getTime() / 1000);
        }
        if (ts != null) {
          const list = latestCandlesRef.current || [];
          const found = list.find((c) => Number(c.time) === Number(ts));
          if (found) {
            isHoveringRef.current = true;
            setHoverInfo({
              time: found.time,
              open: found.open,
              high: found.high,
              low: found.low,
              close: found.close,
              volume: found.volume ?? null,
            });
            return;
          }
        }
      }
      isHoveringRef.current = false;
      setHoverInfo(null);
    });

    const handleResize = () => {
      if (!chartRef.current || !chartContainerRef.current) return;
      chartRef.current.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });
    };
    window.addEventListener("resize", handleResize);

    return () => {
      // cleanup: unsubscribe crosshair and remove chart once
      try {
        unsubscribeCross();
      } catch (e) {
        /* ignore */
      }
      window.removeEventListener("resize", handleResize);
      try {
        chart.remove();
      } catch (e) {
        /* ignore */
      }
      isUnmountedRef.current = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stSegmentSeriesRef.current.forEach((s) => {
        try {
          s.setData([]);
        } catch (e) {}
      });
      chartRef.current = null;
      candleSeriesRef.current = null;
      stSegmentSeriesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, decimalPlaces]);

  // ensure segment series exist (reused)
  const ensureSegmentSeriesCount = (n) => {
    if (!chartRef.current) return;
    try {
      while (stSegmentSeriesRef.current.length < n) {
        const series = chartRef.current.addLineSeries({
          color: "#00ff85",
          lineWidth: 2,
          lastValueVisible: false,
          priceLineVisible: false,
          priceFormat: {
            type: 'price',
            precision: decimalPlaces,
            minMove: getMinMove(decimalPlaces),
          },
        });
        try {
          series.setData([]);
        } catch (e) {
          /* ignore */
        }
        stSegmentSeriesRef.current.push(series);
      }
    } catch (e) {
      /* ignore */
    }
  };

  // update data and SuperTrend segments
  useEffect(() => {
    if (!candleSeriesRef.current || !chartRef.current) return;
    if (isUnmountedRef.current) return;

    const lwc = candles.map((c) => ({
      time:
        typeof c.time === "number"
          ? c.time
          : Math.floor(new Date(c.time).getTime() / 1000),
      open: Number(c.open),
      high: Number(c.high),
      low: Number(c.low),
      close: Number(c.close),
      volume: c.volume != null ? Number(c.volume) : undefined,
    }));

    if (lwc.length === 0) {
      try {
        candleSeriesRef.current.setData([]);
      } catch (e) {}
      stSegmentSeriesRef.current.forEach((s) => {
        try {
          s.setData([]);
        } catch (e) {}
      });
      try {
        candleSeriesRef.current.setMarkers([]);
      } catch (e) {}
      setHoverInfo(null);
      return;
    }

    const stFull = computeSuperTrend(
      lwc,
      superTrendPeriod,
      superTrendMultiplier,
    );

    const segments = [];
    let currentSegment = null;
    for (let i = 0; i < stFull.length; i++) {
      const s = stFull[i];
      if (!s) continue;
      const { time, value, trend } = s;
      if (!currentSegment || currentSegment.trend !== trend) {
        currentSegment = { trend, points: [{ time, value }] };
        segments.push(currentSegment);
      } else {
        currentSegment.points.push({ time, value });
      }
    }

    const markers = [];
    for (let i = 1; i < segments.length; i++) {
      const seg = segments[i];
      const p = seg.points[0];
      markers.push({
        time: p.time,
        position: seg.trend === "up" ? "belowBar" : "aboveBar",
        color: seg.trend === "up" ? "#00ffd0" : "#ff7aa2",
        shape: seg.trend === "up" ? "arrowUp" : "arrowDown",
        text: seg.trend === "up" ? "UP" : "DN",
      });
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (isUnmountedRef.current) return;
      try {
        try {
          candleSeriesRef.current.setData(lwc);
        } catch (e) {}
        latestCandlesRef.current = lwc;
        ensureSegmentSeriesCount(segments.length);
        for (let i = 0; i < stSegmentSeriesRef.current.length; i++) {
          const series = stSegmentSeriesRef.current[i];
          if (i < segments.length) {
            const seg = segments[i];
            try {
              series.applyOptions({
                color: seg.trend === "up" ? "#00ff85" : "#ff2d55",
              });
              series.setData(seg.points);
            } catch (e) {
              /* ignore per-series error */
            }
          } else {
            try {
              series.setData([]);
            } catch (e) {}
          }
        }
        try {
          candleSeriesRef.current.setMarkers(markers);
        } catch (e) {}
      } finally {
        rafRef.current = null;
      }
    });

    if (!isHoveringRef.current) {
      const last = lwc[lwc.length - 1];
      setHoverInfo({
        time: last.time,
        open: last.open,
        high: last.high,
        low: last.low,
        close: last.close,
        volume: last.volume,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, superTrendPeriod, superTrendMultiplier]);

  const fmtTime = (t) => {
    if (!t) return "";
    const d = typeof t === "number" ? new Date(t * 1000) : new Date(t);
    return d.toLocaleString();
  };

  const fmtPrice = (price) => {
    return Number(price).toFixed(decimalPlaces);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height,
        backgroundColor: "rgb(0,0,0)",
      }}
    >
      <div
        ref={chartContainerRef}
        className="chart-container"
        style={{ width: "100%", height }}
      />
      <div
        style={{
          position: "absolute",
          left: 12,
          top: 12,
          zIndex: 100,
          background: "rgba(0,0,0,0.65)",
          border: "1px solid rgba(0,255,200,0.16)",
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 12,
          color: "#00f5d4",
          boxShadow: "0 6px 30px rgba(0,255,200,0.04)",
          minWidth: 170,
          backdropFilter: "blur(4px)",
        }}
      >
        {hoverInfo ? (
          <div>
            <div style={{ fontSize: 11, color: "#8ef0d6", marginBottom: 6 }}>
              {fmtTime(hoverInfo.time)}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ color: "#9beac9" }}>O</div>
              <div style={{ fontWeight: 600, color: "#ffffff" }}>
                {fmtPrice(hoverInfo.open)}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ color: "#9beac9" }}>H</div>
              <div style={{ fontWeight: 600, color: "#ffffff" }}>
                {fmtPrice(hoverInfo.high)}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ color: "#9beac9" }}>L</div>
              <div style={{ fontWeight: 600, color: "#ffffff" }}>
                {fmtPrice(hoverInfo.low)}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ color: "#9beac9" }}>C</div>
              <div
                style={{
                  fontWeight: 700,
                  color:
                    hoverInfo.close >= hoverInfo.open ? "#22ff99" : "#ff6b83",
                }}
              >
                {fmtPrice(hoverInfo.close)}
              </div>
            </div>
            {hoverInfo.volume != null && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#bff9e8" }}>
                Vol: {Number(hoverInfo.volume)}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: "#666" }}>Sin datos</div>
        )}
      </div>
    </div>
  );
}
